import React, { useEffect, useState } from "react";
import dayjs from 'dayjs';
import { Title } from '../../utils/title';
import { fetchApi } from "../../services/api";
import { Loader } from "@progress/kendo-react-indicators";

type CountRow = {
    Class: string;
    Type: string;
    Status: string;
    Count: number;
}

type Section = {
    Classes: string[];
    Types: string[];
    Statuses: string[];
    Rows: CountRow[];
}

type ViewModel = {
    Vehicles: Section;
    Drivers: Section;
    Trailers: Section;
    StatusColors: Record<string, string>;
}

const PivotTable = ({ title, typeLabel, section, statusColors }: { title: string, typeLabel: string, section: Section, statusColors: Record<string, string> }) => {
    const count = (filter: (x: CountRow) => boolean) => section.Rows.filter(filter).reduce((sum, x) => sum + x.Count, 0);
    const cell = (value: number, bold = false, color?: string) => <td className={`text-right${bold || (color && value > 0) ? " font-weight-bold" : ""}${value === 0 ? " text-muted" : ""}`} style={color && value > 0 ? { color } : undefined}>{value.toLocaleString()}</td>;
    const groupBorder = { borderLeft: "2px solid #dee2e6" };
    const hasGroups = section.Classes.length > 0;
    const groups: (string | null)[] = hasGroups ? section.Classes : [null];
    const statusesFor = (c: string | null) => c === "None" ? [] : section.Statuses;
    const inGroup = (x: CountRow, c: string | null) => c === null || x.Class === c;

    const groupCells = (filter: (x: CountRow) => boolean, bold: boolean) => groups.map(c => <React.Fragment key={c ?? ""}>
        {hasGroups && statusesFor(c).map(s => <React.Fragment key={s}>{cell(count(x => filter(x) && inGroup(x, c) && x.Status === s), bold, statusColors[s])}</React.Fragment>)}
        {!hasGroups && section.Statuses.map(s => <React.Fragment key={s}>{cell(count(x => filter(x) && x.Status === s), bold, statusColors[s])}</React.Fragment>)}
        {hasGroups && cell(count(x => filter(x) && inGroup(x, c)), true)}
    </React.Fragment>);

    return <div className="card mb-3">
        <div className="card-header d-flex justify-content-between align-items-baseline">
            <h5 className="mb-0">{title}</h5>
            <div>
                <span className="h5 mb-0">{count(() => true).toLocaleString()}</span>
            </div>
        </div>
        <div className="table-responsive">
            <table className="table table-sm table-hover mb-0">
                <thead>
                    {hasGroups && <tr>
                        <th rowSpan={2} className="align-bottom">{typeLabel}</th>
                        {section.Classes.map(c => <th key={c} colSpan={statusesFor(c).length + 1} className="text-center" style={groupBorder}>{c}</th>)}
                        <th rowSpan={2} className="text-right align-bottom" style={groupBorder}>Total</th>
                    </tr>}
                    <tr>
                        {!hasGroups && <th>{typeLabel}</th>}
                        {hasGroups && section.Classes.map(c => <React.Fragment key={c}>
                            {statusesFor(c).map((s, i) => <th key={s} className="text-right small" style={{ ...(i === 0 ? groupBorder : {}), color: statusColors[s] }}>{s}</th>)}
                            <th className="text-right small" style={statusesFor(c).length === 0 ? groupBorder : undefined}>Total</th>
                        </React.Fragment>)}
                        {!hasGroups && section.Statuses.map(s => <th key={s} className="text-right small" style={{ color: statusColors[s] }}>{s}</th>)}
                        {!hasGroups && <th className="text-right" style={groupBorder}>Total</th>}
                    </tr>
                </thead>
                <tbody>
                    {section.Types.map(t => <tr key={t}>
                        <td>{t}</td>
                        {groupCells(x => x.Type === t, false)}
                        {cell(count(x => x.Type === t), true)}
                    </tr>)}
                </tbody>
                <tfoot>
                    <tr className="font-weight-bold" style={{ borderTop: "2px solid #dee2e6" }}>
                        <td>Total</td>
                        {groupCells(() => true, true)}
                        {cell(count(() => true), true)}
                    </tr>
                </tfoot>
            </table>
        </div>
    </div>;
}

const EquipmentCounts = () => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<ViewModel>();

    useEffect(() => {
        fetchApi("/api/Asset/EquipmentCounts")
            .then((response: ViewModel) => {
                setData(response);
                setLoading(false);
            })
            .catch(err => {
                alert(err);
                setLoading(false);
            });
    }, []);

    return <div className="container-fluid mt-3">
        <Title string="Equipment Counts" />
        {loading || !data
            ? <div className="k-pos-absolute k-top-center mt-5"><Loader type="converging-spinner" /></div>
            : <>
                <PivotTable statusColors={data.StatusColors} title="Vehicles" typeLabel="Vehicle Type" section={data.Vehicles} />
                <PivotTable statusColors={data.StatusColors} title="Drivers" typeLabel="Vehicle Type" section={data.Drivers} />
                <PivotTable statusColors={data.StatusColors} title="Trailers" typeLabel="Trailer Type" section={data.Trailers} />
            </>}
    </div>;
}

export default EquipmentCounts;
